import React, { useMemo, useState } from 'react';
import { BsDownload } from 'react-icons/bs';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, Grid, GridItem, HStack, Icon, IconButton, Input, Text } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import PulseLoader from '../../../../components/PulseLoader';
import LoadingSpinner from '../../../../components/Spinner';
import StatusBadge from '../../../../components/StatusBadge';
import ProgressCircle from '../../../../pages/JobsPage/components/ProgressCircle';
import { createErrorToast, createSuccessToast, createToast } from '../../../../redux/modules/app';
import { selectProcoreSidePanelData } from '../../../../redux/modules/procoreSidePanel';
import { useProcoreProjectDetails, useProcoreProjectRecordDownload, useProcoreProjectRecords } from '../../../../services/integration-service';
import { getInfiniteListData, getInfiniteListRenderedDataLength } from '../../../../services/util';
import colors from '../../../../styles/theme/foundation/colors';
import { debounce } from '../../../../utils/debounce';

const ProjectRecords = ({ pinsCompanyId, procoreProjectId }) => {
  const dispatch = useDispatch();
  const { pinsAccessToken } = useSelector(selectProcoreSidePanelData);
  const [searchTerm, setSearchTerm] = useState(null);
  const recordQuery = useProcoreProjectRecords({ pinsAccessToken, companyId: pinsCompanyId, procoreProjectId, search: searchTerm });
  const { refetch: fetchExcelLink } = useProcoreProjectRecordDownload({ pinsAccessToken, companyId: pinsCompanyId, procoreProjectId, search: searchTerm });
  const { data: records, isLoading: recordsAreLoading, isFetching: recordsAreFetching } = recordQuery;
  const { data: project, isLoading: projectIsLoading } = useProcoreProjectDetails({ pinsAccessToken, companyId: pinsCompanyId, procoreProjectId });

  const data = useMemo(() => getInfiniteListData(records), [records]);

  const dataLength = useMemo(() => getInfiniteListRenderedDataLength(recordQuery), [records]);

  const handleDownloadExcel = async () => {
    dispatch(createToast({ title: 'Downloading report...', duration: 60000 }));

    try {
      const response = await fetchExcelLink();

      if (response?.data?.excelLink) {
        const link = document.createElement('a');
        link.href = response?.data?.excelLink;
        link.setAttribute('download', `Project ${procoreProjectId} - ${Date.now()}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();

        dispatch(createSuccessToast({ title: 'Report downloaded!' }));
      }
    } catch (error) {
      dispatch(createErrorToast({
        title: 'Uh oh...',
        description: 'Something went wrong while downloading your report',
        error
      }));
    }
  };

  const handleSearchRecords = debounce(searchValue => {
    setSearchTerm(searchValue);
  }, 600);

  const handleSearchChange = e => {
    const { value } = e.target;
    handleSearchRecords(value);
  };

  if (!pinsCompanyId || !procoreProjectId) {
    return (
      <Box>
        <Text>No PINS company id or Procore project id provided</Text>
      </Box>
    );
  }

  if (projectIsLoading) {
    return (
      <Box>
        <LoadingSpinner minHeight="200px" />
      </Box>
    );
  }

  if (!project?.jobName) {
    return (
      <Box>
        <Text>No PINS project exists for the given Procore Project Id</Text>
        <Text>{`Procore Project Id: ${procoreProjectId}`}</Text>
      </Box>
    );
  }

  return (
    <Box>
      <HStack
        alignItems="flex-start"
        justifyContent="space-between"
        mb={2}
        spacing={4}
      >
        <Box textAlign="left">
          <Text mb={2} textStyle="bodyXs500">Project Info</Text>
          <Text textStyle="bodyLg500">{project.jobName}</Text>
        </Box>
        <Box>
          <Text mb={2} textStyle="bodyXs500">Approved</Text>
          <ProgressCircle
            isArchived={project.isArchived}
            percentage={project.approvalPercentage}
          />
        </Box>
      </HStack>

      <HStack
        alignItems="flex-start"
        justifyContent="space-between"
        mb={6}
        spacing={4}
        textAlign="left"
      >
        <Text textStyle="bodyMd500">{`Project # ${project.jobNumber}`}</Text>

        <HStack>
          <Input
            bgColor="white"
            placeholder="search"
            variant="standard"
            width="175px"
            onChange={handleSearchChange}
          />
          <IconButton
            aria-label="download-report-button"
            icon={<Icon as={BsDownload} />}
            variant="primary"
            onClick={handleDownloadExcel}
          />
        </HStack>
      </HStack>

      <hr />

      {(recordsAreLoading || recordsAreFetching)
        ? (
          <Box my={8}>
            <PulseLoader loading={recordsAreLoading || recordsAreFetching} />
          </Box>
        )
        : (
          <>
            <Grid
              my={4}
              templateColumns="repeat(10, 1fr)"
              textAlign="left"
            >
              <GridItem colSpan={7}>
                <Text textStyle="bodyXs500">Contract Company</Text>
              </GridItem>
              <GridItem colSpan={3}>
                <Text textStyle="bodyXs500">Status</Text>
              </GridItem>
            </Grid>

            <Box
              id="procore-project-records-scrollable-rows"
              mb={4}
            >
              <InfiniteScroll
                dataLength={dataLength}
                hasMore={recordQuery.hasNextPage}
                height="500px"
                loader={<PulseLoader />}
                maxHeight="100%"
                next={recordQuery.fetchNextPage}
                scrollableTarget="procore-project-records-scrollable-rows"
                scrollThreshold={0.75}
              >
                {data.map(record => (
                  <Box
                    _hover={{ boxShadow: 'accent' }}
                    backgroundColor="white"
                    borderColor={colors.greyScale['200']}
                    borderRadius="10px"
                    borderWidth="1px"
                    cursor="pointer"
                    flexBasis="100%"
                    key={record.id}
                    marginBottom="1rem"
                    paddingBottom="1rem"
                    paddingLeft="0.75rem"
                    paddingRight="0.75rem"
                    paddingTop="1rem"
                    transition="0.25s"
                    onClick={() => {
                      const route = `http://localhost:3000/v2/companies/${pinsCompanyId}/insureds/${record.insured.id}/records/${record.id}`;
                      let sub = null;

                      switch (PINS_ENV) {
                        case 'prod':
                          sub = 'portal';
                          break;
                        case 'qa':
                          sub = 'qa';
                          break;
                        case 'dev':
                          sub = 'dev';
                          break;
                        default:
                          break;
                      }

                      const url = sub ? `https://${sub}.pinsadvantage.com/v2/companies/${pinsCompanyId}/insureds/${record.insured.id}/records/${record.id}` : route;

                      window.open(url, '_blank');
                    }}
                  >
                    <Grid
                      templateColumns="repeat(10, 1fr)"
                    >
                      <GridItem
                        colSpan={7}
                        textAlign="left"
                      >
                        <Text mb={2} textStyle="bodySm500">{record.insured.companyName}</Text>
                        <Text textStyle="bodyXs500">{`Contract # ${record.insuranceRequirement.contractNumber}`}</Text>
                      </GridItem>
                      <GridItem colSpan={3}>
                        <StatusBadge minW="20px" size="sm" status={record.status} />
                      </GridItem>
                    </Grid>
                  </Box>
                ))}
              </InfiniteScroll>
            </Box>

            <Button
              leftIcon={<Icon as={BsDownload} boxSize="20px" mr={4} />}
              variant="primary"
              width="100%"
              onClick={handleDownloadExcel}
            >
              DOWNLOAD
            </Button>
          </>
        )}
    </Box>
  );
};

ProjectRecords.propTypes = {
  pinsCompanyId: PropTypes.number.isRequired,
  procoreProjectId: PropTypes.number.isRequired
};

export default ProjectRecords;
